import { Box, Paper, Tab, Tabs, TextField, Theme, Typography, useMediaQuery, useTheme } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import clsx from 'clsx';
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Notifications } from '../types';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { emailTemplate } from '../assets/emailTemplate';
import { TFunction } from 'i18next';
import { Tag } from 'types/auto-templates';
import { BaseButton } from 'components/BaseButton';
import { AutocompleteVirtualize } from 'views/documents-templates/components/autocomplete-virtualized';
import { ItemTag } from 'views/documents-templates/components/item-tag';
import { ItemTagTextOption } from 'views/documents-templates/components/item-tag-text';

const COARSE_POINTER = '@media (hover: none) and (pointer: coarse)';
const SHORT_LANDSCAPE = '@media (max-height: 500px) and (orientation: landscape)';

// Layout contract: below `md` the editor and the preview stack in ONE column and the pane
// Paper is the single vertical scroll owner; from `md` the two halves share the row 50/50
// and the editor half owns its own scroll. In the create dialog the root is a plain div
// that fills the dialog and the editor half owns the scroll at every width.
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    textarea: {
      height: '150px',
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.background.paper,
      padding: 5,
      border: `1.2px solid ${theme.palette.border.line}`,
      borderRadius: '5px',
      minWidth: '100%',
      maxWidth: '100%',
      fontFamily: 'inherit',
      fontSize: '0.875rem',
    },
    paper: {
      display: 'flex',
      flexDirection: 'column',
      minWidth: 0,
      maxWidth: '100%',
      height: '100%',
      overflowX: 'hidden',
      overflowY: 'auto',
      [theme.breakpoints.up('md')]: {
        overflow: 'hidden',
      },
    },
    paperModal: {
      display: 'flex',
      flexDirection: 'column',
      flex: '1 1 0',
      minHeight: 0,
      minWidth: 0,
      maxWidth: '100%',
      overflow: 'hidden',
    },
    body: {
      display: 'flex',
      flexDirection: 'column',
      flex: '0 0 auto',
      minWidth: 0,
      width: '100%',
      [theme.breakpoints.up('md')]: {
        flexDirection: 'row',
      },
    },
    tabsBar: {
      position: 'sticky',
      top: 0,
      zIndex: 3,
      flexShrink: 0,
      backgroundColor: theme.palette.background.paper,
      borderBottom: `1px solid ${theme.palette.divider}`,
      '& .MuiTab-root': { textTransform: 'none' },
    },
    bodyPane: {
      [theme.breakpoints.up('md')]: {
        flex: '1 1 0',
        minHeight: 0,
      },
    },
    editor: {
      display: 'flex',
      flexDirection: 'column',
      flex: '0 0 auto',
      minWidth: 0,
      padding: theme.spacing(2),
      boxSizing: 'border-box',
      [theme.breakpoints.up('md')]: {
        flex: '1 1 0',
        minHeight: 0,
      },
    },
    editorPane: {
      [theme.breakpoints.up('md')]: {
        overflow: 'auto',
      },
    },
    header: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(1.5),
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      marginBottom: theme.spacing(4),
      minWidth: 0,
    },
    headerItem: {
      flex: '1 1 100%',
      minWidth: 0,
      [theme.breakpoints.up('sm')]: {
        flex: '1 1 0',
      },
    },
    heading: {
      overflowWrap: 'anywhere',
    },
    picker: {
      border: 0,
      margin: 0,
      padding: 0,
      minInlineSize: 0,
      [COARSE_POINTER]: {
        '& .MuiOutlinedInput-root': {
          minHeight: theme.spacing(5.5),
        },
      },
    },
    quillContainer: {
      minWidth: 0,
      maxWidth: '100%',
      '& .ql-toolbar': {
        position: 'sticky',
        top: 0,
        zIndex: 10,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        minWidth: 0,
        maxWidth: '100%',
        boxSizing: 'border-box',
        backgroundColor: theme.palette.background.paper,
      },
      '& .ql-container': {
        fontSize: '16px',
        height: '300px',
        overflow: 'auto',
        maxWidth: '100%',
        boxSizing: 'border-box',
        [SHORT_LANDSCAPE]: {
          height: theme.spacing(22),
        },
      },
      '& .ql-editor': {
        minHeight: '250px',
        [SHORT_LANDSCAPE]: {
          minHeight: 0,
        },
      },
    },
    quillContainerStacked: {
      // The non-modal stacked pane scrolls as a whole (the Paper owns the scroll), so the sticky
      // toolbar must sit below the sticky tabs bar instead of covering it.
      '& .ql-toolbar': { top: theme.spacing(6) },
    },
    saveRow: {
      display: 'flex',
      justifyContent: 'flex-end',
      position: 'sticky',
      bottom: 0,
      zIndex: 5,
      marginTop: theme.spacing(2),
      paddingTop: theme.spacing(1),
      paddingBottom: `calc(${theme.spacing(1)} + env(safe-area-inset-bottom, 0px))`,
      backgroundColor: theme.palette.background.paper,
      [COARSE_POINTER]: {
        '& .MuiButton-root': {
          minHeight: theme.spacing(5.5),
        },
      },
    },
    preview: {
      display: 'flex',
      flex: '0 0 auto',
      minWidth: 0,
      overflow: 'hidden',
      borderTop: `1px solid ${theme.palette.divider}`,
      [theme.breakpoints.up('md')]: {
        flex: '1 1 0',
        minHeight: 0,
        borderTop: 'none',
      },
    },
    iframe: {
      display: 'block',
      width: '100%',
      maxWidth: '100%',
      height: theme.spacing(50),
      minHeight: theme.spacing(50),
      border: 'unset',
      [theme.breakpoints.up('md')]: {
        height: '100%',
        minHeight: 0,
      },
    },
    iframeModal: {
      height: '100%',
      minHeight: 0,
    },
    // Modal-only fill rules: declared after the base classes so they win at equal specificity.
    bodyModal: {
      flex: '1 1 0',
      minHeight: 0,
    },
    editorModal: {
      flex: '1 1 0',
      minHeight: 0,
      overflow: 'auto',
    },
    previewModal: {
      flex: '1 1 0',
      minHeight: 0,
    },
    quillContainerModal: {
      display: 'flex',
      flexDirection: 'column',
      flex: '1 1 0',
      minHeight: 0,
      '& > div': {
        display: 'flex',
        flexDirection: 'column',
        flex: '1 1 0',
        minHeight: 0,
      },
      '& .ql-container': {
        height: 'auto',
        flex: '1 1 0',
        minHeight: 0,
      },
      '& .ql-editor': {
        height: '100%',
        minHeight: 0,
      },
    },
  })
);

type NotificationSelectedProps = {
  notification: Notifications;
  handleUpdateNotification: (data: any) => void;
  t: TFunction;
  tagsField: Tag[];
  isModal?: boolean;
  isSubmitting?: boolean;
};

export type NotificationSelectedHandle = {
  submit: () => void;
};

export const normalizeQuillHtml = (html: string) => {
  html = html
    .replace(/class="ql-align-center"/g, 'style="text-align: center;"')
    .replace(/class="ql-align-right"/g, 'style="text-align: right;"')
    .replace(/class="ql-align-justify"/g, 'style="text-align: justify;"')
    .replace(/class="ql-size-small"/g, 'style="font-size: 0.75em;"')
    .replace(/class="ql-size-large"/g, 'style="font-size: 1.5em;"')
    .replace(/class="ql-size-huge"/g, 'style="font-size: 2.5em;"');

  return html;
};

export const normalizeHtmlQuill = (html: string) => {
  html = html
    .replace(/style="text-align: center;"/g, 'class="ql-align-center"')
    .replace(/style="text-align: right;"/g, 'class="ql-align-right"')
    .replace(/style="text-align: justify;"/g, 'class="ql-align-justify"')
    .replace(/style="font-size: 0.75em;"/g, 'class="ql-size-small"')
    .replace(/style="font-size: 1.5em;"/g, 'class="ql-size-large"')
    .replace(/style="font-size: 2.5em;"/g, 'class="ql-size-huge"');

  return html;
};

const NotificationSelected = forwardRef<NotificationSelectedHandle, NotificationSelectedProps>(({
  notification,
  handleUpdateNotification,
  t,
  tagsField,
  isModal = false,
  isSubmitting = false,
}, ref) => {
  const classes = useStyles();
  const theme = useTheme();
  // Matches the `.body` `[theme.breakpoints.up('md')]` row switch: panes stack below 960px.
  // MUI v4 `down('sm')` is <960px, so `!up('md')` is the clear equivalent (`down('md')` is <1280).
  const isStacked = !useMediaQuery(theme.breakpoints.up('md'), { noSsr: true });
  const [stackedTab, setStackedTab] = useState(0);
  const [value, setValue] = useState('');
  const [subject, setSubject] = useState('');
  const [prompt, setPrompt] = useState('');
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const quillRef = useRef<ReactQuill>(null);
  const loadedTemplateRef = useRef<string | undefined>(undefined);
  const hidePromp = true;
  useEffect(() => {
    // Reload only when the selected template actually changes: a parent re-render that hands us a
    // new object with identical content must not wipe in-progress edits, and the server echoing the
    // saved template back must not move the caret.
    if (loadedTemplateRef.current === notification._id) return;
    loadedTemplateRef.current = notification._id;
    setValue(normalizeHtmlQuill(notification.html));
    setPrompt(notification.prompt);
    // Focusing on load pops the mobile keyboard, and the modal never needs a caret reset.
    if (!isModal) {
      const editor = quillRef.current?.getEditor();
      if (editor) {
        editor.setSelection(0, 0);
      }
    }
  }, [notification._id, notification.html, notification.prompt, isModal]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (iframe && iframe.contentDocument) {
      const doc = iframe.contentDocument;
      const finalHtml = emailTemplate.replace(
        'REPLACE_THIS_WITH_CONTENT',
        normalizeQuillHtml(value)
      );
      doc.open();
      doc.write(finalHtml);
      doc.close();
    }
  }, [value]);

  const handleSubmit = () => {
    if (isSubmitting) return;
    const finalHtml = normalizeQuillHtml(value);
    handleUpdateNotification({
      ...notification,
      html: finalHtml,
      id: notification._id,
      subject: notification._id === '' ? subject : notification.subject,
      prompt
    });
  };

  useImperativeHandle(ref, () => ({ submit: handleSubmit }));

  const Root = (isModal ? 'div' : Paper) as React.ElementType;

  return (
    <Root className={isModal ? classes.paperModal : classes.paper}>
      {isStacked && (
        <Box className={classes.tabsBar}>
          <Tabs value={stackedTab} onChange={(_, tab: number) => setStackedTab(tab)} variant="fullWidth">
            <Tab label={<Typography color="primary">{t('emailTemplates:compose')}</Typography>} value={0} />
            <Tab label={<Typography color="primary">{t('emailTemplates:preview')}</Typography>} value={1} />
          </Tabs>
        </Box>
      )}
      <div className={clsx(classes.body, !isModal && classes.bodyPane, isModal && classes.bodyModal)}>
        <div
          className={clsx(classes.editor, !isModal && classes.editorPane, isModal && classes.editorModal)}
          style={{ display: !isStacked || stackedTab === 0 ? 'flex' : 'none' }}
        >
          <div className={classes.header}>
            {notification._id !== '' ? (
              <Typography className={clsx(classes.headerItem, classes.heading)} variant="h6">
                {notification.subject}
              </Typography>
            ) : (
              <TextField
                className={classes.headerItem}
                label={t('emailTemplates:subject')}
                variant="filled"
                value={subject}
                disabled={isSubmitting}
                onChange={(e) => {
                  setSubject(e.target.value);
                }}
              />
            )}
            {/* AutocompleteVirtualize has no `disabled` prop (shared component, not editable here):
                a disabled fieldset natively disables its input and buttons while saving. */}
            <fieldset className={clsx(classes.headerItem, classes.picker)} disabled={isSubmitting}>
              <AutocompleteVirtualize
                options={tagsField}
                label={t('documents:field-info')}
                renderOption={(tag) => (
                  <ItemTag
                    key={tag._id}
                    tag={tag}
                    options={() => <ItemTagTextOption tagId={tag._id} />}
                  />
                )}
              />
            </fieldset>
          </div>
          <Box className={clsx(classes.quillContainer, isModal && classes.quillContainerModal, isStacked && !isModal && classes.quillContainerStacked)}>
            <ReactQuill
              theme="snow"
              ref={quillRef}
              value={value}
              onChange={setValue}
              readOnly={isSubmitting}
              modules={{
                toolbar: [
                  [{ size: [] }, { align: [] }],
                  ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                  [{ color: [] }, { background: [] }],
                  [{ list: 'ordered' }, { list: 'bullet' }],
                  ['link'],
                  ['clean'],
                ],
              }}
              formats={[
                'size',
                'align',
                'bold',
                'italic',
                'underline',
                'strike',
                'blockquote',
                'color',
                'background',
                'list',
                'bullet',
                'link',
                'clean',
              ]}
            />
          </Box>
          {!hidePromp &&
            <Box style={{ marginTop: "10px" }}>
              <Typography variant="body1" color="primary">
                {t('emailTemplates:prompt')}
              </Typography>
              <textarea
                className={classes.textarea}
                placeholder=""
                style={{ marginTop: "5px", height: "auto" }}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
              />
            </Box>}
        </div>
        <div
          className={clsx(classes.preview, isModal && classes.previewModal)}
          style={{ display: !isStacked || stackedTab === 1 ? 'flex' : 'none' }}
        >
          <iframe
            ref={iframeRef}
            title="preview"
            className={clsx(classes.iframe, isModal && classes.iframeModal)}
          />
        </div>
      </div>
      {!isModal && (
        <div className={classes.saveRow}>
          <BaseButton
            icon="check"
            text={t('common:save')}
            ariaLabel={t('common:save')}
            isLoading={isSubmitting}
            disabled={isSubmitting}
            onClick={handleSubmit}
          />
        </div>
      )}
    </Root>
  );
});

export default NotificationSelected;
